/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */
import React from "react";
import { Tooltip, TextField, InputAdornment, MenuItem, CardContent, Grid } from "@material-ui/core";
import { EMAIL_TOOL_TIPS, WEB_CHAT_TOOL_TIPS } from "../../../../utility/toolTipMessages";
import FormTextField from "../../../../components/FormTextField/FormTextField";
import { ActionButtons } from "../../../../components/Common";
import { ConfirmDialog } from "../../../../components/ConfirmDialog/ConfirmDialog";
import { useState } from "react";

/**
 * This component houses fields for the Azure Bot integrations
 * @param {(valueName, newValue) => void} updateAppSettings This is a callback method for setting the values in appSettings
 * @param {Object} appSettings This is the application configuration for the azure bot application.
 * @param {Object} appConfigs This is the entire list of integrations used fo rerror checking.
 * @param {(Boolean) => void} setFormEdited Callback method for setting the form as edited.
 * @param {Boolean} formEdited A boolean value denoting whether the form has been edited or not.
 * @param {Array} contactFlows An array of all the available contact flows for the connect instance.
 * @param {String} selectedAppId The currently selected application ID used when validation checking.
 */
export const EmailIntegrationDetails = ({
	updateAppSettings,
	appSettings,
	appConfigs,
	setFormEdited,
	formEdited,
	contactFlows,
	emailDomains,
	selectedAppId,
	deleteFunction,
	onSave,
	onCancel,
	saveDisabled,
}) => {
	const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);

	const handleRemove = () => {
		setRemoveConfirmOpen(true);
	};

	const uniqueEmailError = Object.values(appConfigs).find(
		(val) =>
			val?.applicationId === `${appSettings.emailPrefix}@${appSettings.domainName}` &&
			val?.applicationId !== selectedAppId,
	);

	let emailHelperText = "";
	if (!appSettings.emailPrefix || appSettings.emailPrefix.length === 0) {
		emailHelperText = "An email prefix is required";
	} else if (!appSettings.domainName || appSettings.domainName.length === 0) {
		emailHelperText = "A domain name is required";
	} else if (uniqueEmailError) {
		emailHelperText = "The email address must be unique";
	}

	return (
		<CardContent>
			<Grid container alignItems="center" justifyContent="space-between" wrap="wrap">
				<h5 style={{ fontWeight: "bold", margin: 0 }}>
					{appConfigs[selectedAppId] ? "Edit Application" : "New Application"}
				</h5>
				<ActionButtons
					showCancel
					className="my-2 my-md-0"
					onCancel={onCancel}
					showRemove={appConfigs[selectedAppId]}
					onRemove={handleRemove}
					showSave={formEdited}
					onSave={onSave}
					disableSave={saveDisabled}
				/>
			</Grid>
			<Grid container justifyContent="space-between" spacing={2} className="mt-5">
				<Grid item xs={12} lg={6} className="mt-4">
					<FormTextField
						label={"Friendly SES Application Name"}
						formEdited={formEdited}
						setEdited={setFormEdited}
						styleObject={{ width: "100%" }}
						startingValue={appSettings.friendlyName || ""}
						setConfigValue={updateAppSettings}
						valueName={"friendlyName"}
						enableDebounce
						required
						errorCheck={(newValue) => {
							if (newValue) {
								const newValueLowerCase = newValue.toLowerCase();
								return Object.values(appConfigs).find(
									(val) =>
										val?.friendlyName?.toLowerCase() === newValueLowerCase && val?.applicationId !== selectedAppId,
								);
							}
							return true;
						}}
						toolTipMessage={EMAIL_TOOL_TIPS.FRIENDLY_NAME}
						errorMessage={"Friendly Name is required and must be unique."}
					/>
				</Grid>
				<Grid item xs={12} lg={6} className="mt-4">
					{contactFlows && (
						<TextField
							label={"Contact Flow"}
							select
							style={{ width: "100%" }}
							value={appSettings.contactFlowId || ""}
							error={!appSettings.contactFlowId || appSettings.contactFlowId.length === 0}
							onChange={(event) => {
								const edited = appSettings.contactFlowId !== event.target.value;
								if (edited) {
									updateAppSettings("contactFlowId", event.target.value);
									setFormEdited(true);
								}
							}}
							helperText={
								(!appSettings.contactFlowId || appSettings.contactFlowId.length === 0) && "A Contact Flow is required"
							}
							InputProps={{
								startAdornment: (
									<InputAdornment position="start">
										<Tooltip arrow interactive placement={"bottom-start"} title={WEB_CHAT_TOOL_TIPS.CONTACT_FLOW}>
											<i style={{ height: "15px", color: "gray" }} className="fas fa-info-circle" />
										</Tooltip>
									</InputAdornment>
								),
							}}
						>
							{(contactFlows || []).map((flow) => {
								return (
									<MenuItem key={flow.Id} value={flow.Id}>
										{flow.Name}
									</MenuItem>
								);
							})}
						</TextField>
					)}
				</Grid>
				<Grid item xs={12} lg={6} className="mt-4">
					{emailDomains && (
						<>
							<TextField
								label={"Email Address"}
								value={appSettings.emailPrefix || ""}
								error={emailHelperText !== ""}
								style={{ width: "50%" }}
								onChange={(event) => {
									const edited = appSettings.emailPrefix !== event.target.value;
									if (edited) {
										updateAppSettings("emailPrefix", event.target.value);
										setFormEdited(true);
									}
								}}
								helperText={emailHelperText}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<Tooltip arrow interactive placement={"bottom-start"} title={EMAIL_TOOL_TIPS.EMAIL_ADDRESS}>
												<i style={{ height: "15px", color: "gray" }} className="fas fa-info-circle" />
											</Tooltip>
										</InputAdornment>
									),
								}}
							/>

							<TextField
								select
								style={{ marginTop: "16px", width: "50%" }}
								value={appSettings.domainName || ""}
								error={emailHelperText !== ""}
								onChange={(event) => {
									const edited = appSettings.domainName !== event.target.value;
									if (edited) {
										updateAppSettings("domainName", event.target.value);
										setFormEdited(true);
									}
								}}
								InputProps={{
									startAdornment: <InputAdornment position="start">@</InputAdornment>,
								}}
							>
								{(emailDomains || []).map((domain, idx) => {
									return (
										<MenuItem key={idx} value={domain}>
											{domain}
										</MenuItem>
									);
								})}
							</TextField>
						</>
					)}
				</Grid>
			</Grid>
			<ConfirmDialog
				title={"Remove Application?"}
				message={`Are you sure you wish to delete the application: ${appSettings.friendlyName}?`}
				confirmOpen={removeConfirmOpen}
				onClose={async (confirmed) => {
					if (confirmed) {
						deleteFunction();
					}
					setRemoveConfirmOpen(false);
				}}
			/>
		</CardContent>
	);
};
